import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { accountManagerCandidatesQuery, useAssignAccountManager } from '../../lib/directory-api';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { roleLabel } from '../../lib/directory-display';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog } from '../ui/dialog';
import { SearchInput } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';
import { ErrorState } from '../ui/state-views';

export interface AssignAmDialogProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  /** Where it is, shown beside the name ("Sri Lakshmi Sweets · Ameerpet"). */
  place?: string | null;
  /** Who is primary now, shown as already chosen. */
  currentPrimaryId?: string | null;
  /** True when the business has no manager yet: the first one is always primary. */
  hasNoManager: boolean;
}

function Form({
  onClose,
  businessId,
  businessName,
  currentPrimaryId,
  hasNoManager,
}: Omit<AssignAmDialogProps, 'open'>) {
  const toast = useToast();
  const candidates = useQuery(accountManagerCandidatesQuery);
  const assign = useAssignAccountManager(businessId);
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [primary, setPrimary] = useState(true);
  const [error, setError] = useState<string>();

  const people = (candidates.data ?? []).filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()));
  const chosenPerson = candidates.data?.find((p) => p.id === chosen);

  const submit = async () => {
    if (!chosenPerson) return;
    setError(undefined);
    try {
      await assign.mutateAsync({ staffId: chosenPerson.id, makePrimary: primary || hasNoManager });
      toast({
        title: 'Account manager assigned',
        description: `${chosenPerson.name} now looks after ${businessName}.`,
      });
      onClose();
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.[0]) setError(e.fieldErrors[0].message);
      else if (e.kind === 'forbidden') setError('Only admins can assign account managers.');
      else if (e.kind === 'not_found') setError('This business no longer exists.');
      else if (e.kind === 'unauthorized') setError('Your session expired. Please sign in again.');
      else setError('We couldn’t assign them. Please try again.');
    }
  };

  return (
    <div className="flex flex-col gap-(--mbm-space-4)">
      {error ? <Banner tone="error">{error}</Banner> : null}
      <SearchInput
        aria-label="Search account managers"
        placeholder="Search account managers"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
      />

      {candidates.isLoading ? (
        <div className="flex flex-col gap-(--mbm-space-2)">
          <Skeleton className="h-(--mbm-size-list-row-default) w-full" />
          <Skeleton className="h-(--mbm-size-list-row-default) w-full" />
        </div>
      ) : candidates.isError ? (
        <ErrorState
          title="Couldn’t load the team"
          className="py-(--mbm-space-5)"
          onRetry={() => void candidates.refetch()}
        />
      ) : people.length === 0 ? (
        <p className="py-(--mbm-space-4) text-center text-body-medium text-text-secondary">
          {search ? 'Nobody matches that.' : 'There are no account managers yet. Add one from Staff.'}
        </p>
      ) : (
        <ul
          role="radiogroup"
          aria-label="Account manager"
          className="flex max-h-(--mbm-layout-dialog-list-max-height) flex-col gap-(--mbm-space-2) overflow-y-auto"
        >
          {people.map((person) => {
            const selected = chosen === person.id;
            return (
              <li key={person.id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setChosen(person.id)}
                  className={cx(
                    'flex w-full items-center gap-(--mbm-space-3) rounded-(--mbm-component-radius-input) border-[1.5px] bg-surface-card p-(--mbm-space-3) text-left focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
                    selected ? 'border-action-primary' : 'border-border-line',
                  )}
                >
                  <Avatar tone="neutral" initials={initialsOf(person.name)} size="sm" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-label-large text-text-primary">{person.name}</span>
                    <span className="text-body-small text-text-secondary">
                      {roleLabel(person.staffRole)} · {person.clientCount}{' '}
                      {person.clientCount === 1 ? 'client' : 'clients'}
                      {person.id === currentPrimaryId ? ' · primary now' : ''}
                    </span>
                  </span>
                  {selected ? <Icon icon={CheckCircle} size="lg" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-body-xsmall text-text-tertiary">Only account managers and admins are listed.</p>

      {/* The first manager a business gets is always primary, so for them the box is shown ticked and fixed. */}
      <Checkbox
        label={
          <span className="flex flex-col">
            <span className="text-label-medium text-text-primary">Make primary account manager</span>
            <span className="text-body-small text-text-secondary">
              The client sees this person as their main contact.
            </span>
          </span>
        }
        checked={primary || hasNoManager}
        disabled={hasNoManager}
        onChange={(e) => setPrimary(e.target.checked)}
      />

      <div className="rounded-(--mbm-radius-md) bg-surface-muted p-(--mbm-space-4)">
        <p className="text-label-small text-text-tertiary">When you assign</p>
        <p className="text-body-small text-text-primary">
          They see this client and all its work.{' '}
          {primary || hasNoManager ? 'Any current primary stays on as a backup. ' : ''}This change is saved to the audit
          log.
        </p>
      </div>

      <div className="flex flex-col-reverse gap-(--mbm-space-2) sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose} disabled={assign.isPending}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} disabled={!chosenPerson} loading={assign.isPending}>
          {chosenPerson ? `Assign ${chosenPerson.name}` : 'Assign'}
        </Button>
      </div>
    </div>
  );
}

/** Figma "Assign account manager" (A03.1): pick from the account managers and admins, optionally as the primary one. */
export function AssignAmDialog({ open, ...rest }: AssignAmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={rest.onClose}
      size="wide"
      title="Assign account manager"
      description={[rest.businessName, rest.place].filter(Boolean).join(' · ')}
    >
      <Form {...rest} />
    </Dialog>
  );
}
